import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';

interface ActivityTypeChartProps {
  data: {
    [activityType: string]: {
      scope1: number;
      scope2: number;
      scope3: number;
      total: number;
    };
  } | null;
  selectedYear: number;
}

interface ActivityData {
  activityType: string;
  total: number;
  scope1: number;
  scope2: number;
  scope3: number;
  color: string;
}

// Activity type name mapping based on actual database values
const ACTIVITY_NAMES: { [key: string]: string } = {
  'LPG (Liquefied Petroleum Gas)': 'LPG (Liquefied Petroleum Gas)',
  'LPG': 'LPG',
  'Diesel': 'Diesel',
  'Coal': 'Coal',
  'Solar': 'Solar',
  'Grid': 'Grid',
  'Waste': 'Waste',
  'Employee Commuting': 'Employee Commuting',
  'electricity_consumption': 'Electricity Consumption',
  'natural_gas_consumption': 'Natural Gas Consumption',
  'fuel_consumption': 'Fuel Consumption',
  'vehicle_emissions': 'Vehicle Emissions',
  'waste_disposal': 'Waste Disposal',
  'business_travel': 'Business Travel',
  'purchased_goods': 'Purchased Goods',
  'capital_goods': 'Capital Goods',
  'fuel_energy_activities': 'Fuel & Energy Activities',
  'transportation': 'Transportation',
  'upstream_transportation': 'Upstream Transportation',
  'waste_generated': 'Waste Generated',
  'water_consumption': 'Water Consumption',
  'refrigerants': 'Refrigerants',
  'fire_suppression': 'Fire Suppression',
  'other': 'Other Activities'
};

// Color scheme for different activity types - All Green Theme
const ACTIVITY_COLORS = [
  '#22c55e', // Green 500
  '#16a34a', // Green 600
  '#15803d', // Green 700
  '#166534', // Green 800
  '#14532d', // Green 900
  '#4ade80', // Green 400
  '#86efac', // Green 300
  '#bbf7d0', // Green 200
  '#dcfce7', // Green 100
  '#f0fdf4', // Green 50
  '#10b981', // Emerald 500
  '#059669', // Emerald 600
  '#047857', // Emerald 700
  '#065f46', // Emerald 800
  '#064e3b', // Emerald 900
  '#34d399', // Emerald 400
  '#6ee7b7', // Emerald 300
  '#a7f3d0'  // Emerald 200
];

export function ActivityTypeChart({ data, selectedYear }: ActivityTypeChartProps) {
  // Debug: Log the incoming data
  console.log('ActivityTypeChart - Incoming data:', data);
  console.log('ActivityTypeChart - Selected year:', selectedYear);

  // Transform data for the chart
  let chartData: ActivityData[] = [];
  
  if (data && typeof data === 'object') {
    chartData = Object.entries(data)
      .map(([activityType, emissions], index) => {
        // Get readable activity name
        let activityName = ACTIVITY_NAMES[activityType];
        if (!activityName) {
          // If activity type is already readable, use it as is
          if (activityType && typeof activityType === 'string' && activityType.length > 0) {
            activityName = activityType;
          } else {
            // Convert snake_case to Title Case as fallback
            activityName = activityType
              .replace(/_/g, ' ')
              .replace(/\b\w/g, l => l.toUpperCase());
          }
        }
        
        return {
          activityType: activityName,
          total: emissions.total,
          scope1: emissions.scope1,
          scope2: emissions.scope2,
          scope3: emissions.scope3,
          color: ACTIVITY_COLORS[index % ACTIVITY_COLORS.length]
        };
      })
      .filter(item => item.total > 0)
      .sort((a, b) => b.total - a.total); // Sort by total emissions descending
  }

  // Debug: Log the transformed chart data
  console.log('ActivityTypeChart - Transformed chart data:', chartData);

  // Always show sample data for demonstration based on actual database
  if (chartData.length === 0) {
    console.log('ActivityTypeChart - No real data, showing sample data');
    chartData = [
      {
        activityType: 'Grid',
        total: 3600,
        scope1: 0,
        scope2: 3600,
        scope3: 0,
        color: ACTIVITY_COLORS[0]
      },
      {
        activityType: 'Diesel',
        total: 1386,
        scope1: 1386,
        scope2: 0,
        scope3: 0,
        color: ACTIVITY_COLORS[1]
      },
      {
        activityType: 'LPG',
        total: 1250,
        scope1: 1250,
        scope2: 0,
        scope3: 0,
        color: ACTIVITY_COLORS[2]
      },
      {
        activityType: 'Coal',
        total: 1250,
        scope1: 1250,
        scope2: 0,
        scope3: 0,
        color: ACTIVITY_COLORS[3]
      },
      {
        activityType: 'Solar',
        total: 400,
        scope1: 0,
        scope2: 400,
        scope3: 0,
        color: ACTIVITY_COLORS[4]
      },
      {
        activityType: 'Waste',
        total: 150,
        scope1: 0,
        scope2: 0,
        scope3: 150,
        color: ACTIVITY_COLORS[5]
      },
      {
        activityType: 'Employee Commuting',
        total: 125,
        scope1: 0,
        scope2: 0,
        scope3: 125,
        color: ACTIVITY_COLORS[6]
      }
    ];
  }

  // Debug: Log final chart data
  console.log('ActivityTypeChart - Final chart data:', chartData);


  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-4 border-2 border-gray-300 rounded-xl shadow-xl max-w-xs">
          <p className="font-bold text-gray-900 text-base mb-3">{label}</p>
          
          <div className="space-y-2">
            <div className="flex justify-between items-center bg-gray-50 p-2 rounded">
              <span className="text-sm text-gray-600">Total Emissions:</span>
              <span className="font-semibold text-gray-900">
                {(data.total / 1000).toFixed(2)}t CO₂e
              </span>
            </div>
            
            <div className="space-y-1">
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  <span className="text-xs text-gray-500">Scope 1 (Direct):</span>
                </div>
                <span className="text-xs font-medium text-gray-700">
                  {(data.scope1 / 1000).toFixed(2)}t
                </span>
              </div>
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                  <span className="text-xs text-gray-500">Scope 2 (Energy):</span>
                </div>
                <span className="text-xs font-medium text-gray-700">
                  {(data.scope2 / 1000).toFixed(2)}t
                </span>
              </div>
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                  <span className="text-xs text-gray-500">Scope 3 (Other):</span>
                </div>
                <span className="text-xs font-medium text-gray-700">
                  {(data.scope3 / 1000).toFixed(2)}t
                </span>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  // Custom label for bars - shows activity name and value
  const CustomLabel = ({ x, y, width, value, payload }: any) => {
    if (value > 0 && payload) {
      const activityName = payload.activityType;
      const shortName = activityName.length > 12 ? activityName.substring(0, 12) + '...' : activityName;
      
      return (
        <g>
          {/* Activity name on top of bar */}
          <text
            x={x + width / 2}
            y={y - 15}
            textAnchor="middle"
            className="text-xs font-semibold fill-gray-800"
            style={{ fontSize: '10px' }}
          >
            {shortName}
          </text>
          {/* Emission value below activity name */}
          <text
            x={x + width / 2}
            y={y - 5}
            textAnchor="middle"
            className="text-xs font-medium fill-gray-600"
            style={{ fontSize: '9px' }}
          >
            {(value / 1000).toFixed(1)}t
          </text>
        </g>
      );
    }
    return null;
  };

  return (
    <div className="w-full">
      <div className="h-96">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{
              top: 40,
              right: 30,
              left: 20,
              bottom: 800,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis 
              dataKey="activityType"
              tick={{ fontSize: 11, fill: '#6b7280' }}
              angle={-45}
              textAnchor="end"
              height={120}
              interval={0}
              label={{ 
                value: 'Activity Type', 
                position: 'insideBottom',
                offset: -5,
                style: { textAnchor: 'middle', fill: '#6b7280' }
              }}
              tickFormatter={(value) => {
                // Ensure we're displaying the actual activity name
                if (!value || value === '') {
                  return 'Unknown Activity';
                }
                // Show full activity names, truncate only if very long
                return value.length > 18 ? value.substring(0, 18) + '...' : value;
              }}
            />
            <YAxis 
              tick={{ fontSize: 12, fill: '#6b7280' }}
              label={{ 
                value: 'Emissions (tCO₂e)', 
                angle: -90, 
                position: 'insideLeft',
                style: { textAnchor: 'middle', fill: '#6b7280' }
              }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ paddingTop: '30px' }}
              formatter={(value) => <span style={{ color: '#374151' }}>{value}</span>}
            />
            <Bar 
              dataKey="total" 
              name="Total Emissions"
              radius={[6, 6, 0, 0]}
              maxBarSize={60}
              label={<CustomLabel />}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

    </div>
  );
}